import {
  insertIntoCell,
  insertIntoRange,
  addColumn,
  addSheet,
  selectRange,
} from "./office";

// =============================================================================
// Action types — discriminated union, schema-validated at parse time
// =============================================================================

export type InsertIntoCellAction = {
  type: "insert_into_cell";
  address: string;
  value: string | number;
};

export type InsertIntoRangeAction = {
  type: "insert_into_range";
  address: string;
  values: unknown[][];
};

export type AddColumnAction = {
  type: "add_column";
  after_column: string;
  header: string;
  formula: string;
};

export type AddSummarySheetAction = {
  type: "add_summary_sheet";
  name: string;
  content: unknown[][];
};

export type SelectRangeAction = {
  type: "select_range";
  address: string;
};

export type ParsedAction =
  | InsertIntoCellAction
  | InsertIntoRangeAction
  | AddColumnAction
  | AddSummarySheetAction
  | SelectRangeAction;

// =============================================================================
// Parse
// =============================================================================

export type ProseSegment = { kind: "prose"; text: string };
export type ActionSegment = { kind: "action"; action: ParsedAction };
export type Segment = ProseSegment | ActionSegment;

export type ParseResult = {
  text: string; // prose-only, action blocks stripped
  actions: ParsedAction[];
  segments: Segment[];
  errors: string[];
};

const ACTION_FENCE = /```myai-action\n([\s\S]*?)\n```/g;

export function parseActionsFromMessage(message: string): ParseResult {
  const segments: Segment[] = [];
  const actions: ParsedAction[] = [];
  const errors: string[] = [];

  let lastIndex = 0;
  for (const match of message.matchAll(ACTION_FENCE)) {
    const idx = match.index ?? 0;
    if (idx > lastIndex) {
      const prose = message.slice(lastIndex, idx).trim();
      if (prose) segments.push({ kind: "prose", text: prose });
    }
    const json = match[1];
    const validated = validateActionJson(json);
    if (validated.ok) {
      segments.push({ kind: "action", action: validated.action });
      actions.push(validated.action);
    } else {
      errors.push(validated.error);
    }
    lastIndex = idx + match[0].length;
  }
  if (lastIndex < message.length) {
    const tail = message.slice(lastIndex).trim();
    if (tail) segments.push({ kind: "prose", text: tail });
  }

  const text = segments
    .filter((s): s is ProseSegment => s.kind === "prose")
    .map((s) => s.text)
    .join("\n\n");

  return { text, actions, segments, errors };
}

type ValidationResult =
  | { ok: true; action: ParsedAction }
  | { ok: false; error: string };

function validateActionJson(json: string): ValidationResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (e) {
    return { ok: false, error: `JSON parse error: ${e instanceof Error ? e.message : "unknown"}` };
  }
  if (typeof parsed !== "object" || parsed === null) {
    return { ok: false, error: "Action payload must be a JSON object." };
  }
  const obj = parsed as Record<string, unknown>;
  const type = obj.type;
  switch (type) {
    case "insert_into_cell":
      if (typeof obj.address !== "string" || (typeof obj.value !== "string" && typeof obj.value !== "number")) {
        return { ok: false, error: "insert_into_cell requires address (string) and value (string|number)." };
      }
      return { ok: true, action: { type, address: obj.address, value: obj.value } };
    case "insert_into_range":
      if (typeof obj.address !== "string" || !Array.isArray(obj.values)) {
        return { ok: false, error: "insert_into_range requires address (string) and values (2D array)." };
      }
      return { ok: true, action: { type, address: obj.address, values: obj.values as unknown[][] } };
    case "add_column":
      if (
        typeof obj.after_column !== "string" ||
        typeof obj.header !== "string" ||
        typeof obj.formula !== "string"
      ) {
        return { ok: false, error: "add_column requires after_column, header, formula (all strings)." };
      }
      return {
        ok: true,
        action: { type, after_column: obj.after_column, header: obj.header, formula: obj.formula },
      };
    case "add_summary_sheet":
      if (typeof obj.name !== "string" || !Array.isArray(obj.content)) {
        return { ok: false, error: "add_summary_sheet requires name (string) and content (2D array)." };
      }
      return { ok: true, action: { type, name: obj.name, content: obj.content as unknown[][] } };
    case "select_range":
      if (typeof obj.address !== "string") {
        return { ok: false, error: "select_range requires address (string)." };
      }
      return { ok: true, action: { type, address: obj.address } };
    default:
      return { ok: false, error: `Unknown action type: ${String(type)}` };
  }
}

// =============================================================================
// Dispatch — execute an action via Office.js
// =============================================================================

export async function executeAction(action: ParsedAction): Promise<void> {
  switch (action.type) {
    case "insert_into_cell":
      return insertIntoCell(action.address, action.value);
    case "insert_into_range":
      return insertIntoRange(action.address, action.values);
    case "add_column":
      return addColumn({
        afterColumn: action.after_column,
        header: action.header,
        formula: action.formula,
      });
    case "add_summary_sheet":
      return addSheet({ name: action.name, content: action.content });
    case "select_range":
      return selectRange(action.address);
  }
}

export function describeAction(action: ParsedAction): string {
  switch (action.type) {
    case "insert_into_cell":
      return `Insert ${typeof action.value === "string" && action.value.startsWith("=") ? "formula" : "value"} into ${action.address}`;
    case "insert_into_range": {
      const rows = action.values.length;
      const cols = action.values[0]?.length ?? 0;
      return `Fill ${rows} × ${cols} cells starting at ${action.address}`;
    }
    case "add_column":
      return `Add column "${action.header}" after ${action.after_column}`;
    case "add_summary_sheet":
      return `Create new sheet "${action.name}" with ${action.content.length} rows`;
    case "select_range":
      return `Navigate to ${action.address}`;
  }
}
